import {
	researchArtifactTargets,
	researchScopeKey,
	type AnalysisSummary,
	type ImpactItem,
	type RecordRead,
	type ResearchCapabilities,
	type ResearchNotice,
	type ResearchScopeRef,
	type ResearchView,
	type StoredRecord,
} from "../../../shared/wire/research.ts";
import type { ProjectSummary } from "../../../shared/wire/projects.ts";
import type { ProfileSummary } from "../../../shared/wire/profiles.ts";
import { decisionRequest, noticeLabel, recordStatusLabel, unavailableNotices } from "../components/researchLabels.ts";

// What the research page shows and sends, apart from how it draws it.

export const LOAD_LABELS = { loading: "加载中…", failed: "加载失败" } as const;
export const BUSY_LABELS = { confirm: "确认中…", reject: "拒绝中…", acknowledge: "提交中…" } as const;

/** The order kinds are listed in; a kind not named here comes after them. */
const KIND_ORDER = ["decision", "hypothesis", "verification_report", "claim", "source", "draft", "evidence", "assessment", "acknowledgment", "run", "plan", "input", "environment", "feedback", "regression_case", "quality_comparison", "material_binding"];

export interface ScopeOption {
	key: string;
	scope: ResearchScopeRef;
	label: string;
}

/**
 * The scopes the switcher offers: the person's inbox, then each of their own projects on a profile with the
 * research tool group. A scope a link points at is kept even before the lists that would name it have loaded.
 */
export function researchScopes(me: string, projects: ProjectSummary[], profiles: Array<Pick<ProfileSummary, "name" | "tools">>, linked?: ResearchScopeRef): ScopeOption[] {
	const research = new Set(profiles.filter((p) => p.tools.includes("research")).map((p) => p.name));
	const options: ScopeOption[] = [{ key: "unassigned", scope: { kind: "unassigned", collectionId: "inbox" }, label: "收件箱" }];
	for (const p of projects) {
		if (p.owner !== me || !research.has(p.profile)) continue;
		const scope: ResearchScopeRef = { kind: "project", projectId: p.id };
		options.push({ key: researchScopeKey(scope), scope, label: p.archived ? `${p.title} · 已归档` : p.title });
	}
	if (linked && !options.some((o) => o.key === researchScopeKey(linked))) {
		options.push({ key: researchScopeKey(linked), scope: linked, label: linked.kind === "project" ? linked.projectId : "收件箱" });
	}
	return options;
}

/** A revision's state; one written by a schema this server does not know is unsupported, whatever it says. */
export function revisionState(record: StoredRecord): string {
	return record.unsupported ? "未支持" : recordStatusLabel(record.status);
}

/** The newest revision of a record as it reads. */
export const newestOf = (read: RecordRead): StoredRecord => read.latest ?? read.record;

/** Whether a record waits on the owner: its newest revision is a proposal. */
export const isPending = (read: RecordRead): boolean => {
	const newest = newestOf(read);
	return !newest.unsupported && newest.status === "proposed";
};

/** The list GET /records answers, as each record reads: its effective revision and the newest one beside it. */
export function recordReads(list: { records: StoredRecord[]; latest: StoredRecord[] }): RecordRead[] {
	const latest = new Map(list.latest.map((r) => [r.id, r]));
	return list.records.map((record) => {
		const newest = latest.get(record.id);
		return newest ? { record, latest: newest } : { record };
	});
}

export interface KindGroup {
	kind: string;
	reads: RecordRead[];
}

/** Records by kind, each kind's waiting ones first, then the rest, newest first. */
export function recordGroups(reads: readonly RecordRead[]): KindGroup[] {
	const byKind = new Map<string, RecordRead[]>();
	for (const r of reads) byKind.set(r.record.kind, [...(byKind.get(r.record.kind) ?? []), r]);
	const rank = (kind: string) => KIND_ORDER.indexOf(kind) + 1 || KIND_ORDER.length + 1;
	return [...byKind.keys()]
		.sort((a, b) => rank(a) - rank(b))
		.map((kind) => {
			const newestFirst = [...byKind.get(kind)!].reverse();
			return { kind, reads: [...newestFirst.filter(isPending), ...newestFirst.filter((r) => !isPending(r))] };
		});
}

/**
 * What confirming or rejecting the revision on screen sends, or null when it cannot be decided: only the newest
 * revision, while proposed, in a schema this server knows. The request names the revision on screen, so one
 * written since is refused by the server instead of decided unseen.
 */
export function decisionFor(displayed: StoredRecord, newest: StoredRecord) {
	if (displayed.unsupported || newest.unsupported) return null;
	// A comparison is the server's re-grading of outputs, not a proposal for the owner to decide.
	if (displayed.kind === "quality_comparison") return null;
	if (displayed.revision !== newest.revision || displayed.status !== "proposed") return null;
	return { confirm: decisionRequest(displayed), reject: (reason: string) => decisionRequest(displayed, reason) };
}

/** A failed decision as the page shows it; a 409 means the revision on screen is no longer the newest, and the page reloads. */
export function decisionFailure(action: "confirm" | "reject", revision: number, err: unknown): { stale: boolean; message: string } {
	const what = action === "confirm" ? "确认失败" : "拒绝失败";
	if ((err as { status?: unknown } | null)?.status === 409) return { stale: true, message: `${what}：r${revision} 已不是最新版本，已重新加载` };
	return { stale: false, message: `${what}：${err instanceof Error ? err.message : String(err)}` };
}

export interface HistoryRow {
	record: StoredRecord;
	state: string;
	/** The revision the record reads as: the confirmed one, else the newest. */
	effective: boolean;
}

/** Every revision, newest first. */
export function historyRows(revisions: StoredRecord[], read: RecordRead): HistoryRow[] {
	return [...revisions]
		.sort((a, b) => b.revision - a.revision)
		.map((record) => ({ record, state: revisionState(record), effective: record.revision === read.record.revision }));
}

export interface PageNotice {
	kind: ResearchNotice["kind"];
	heading: string;
	message: string;
}

/** Every domain the scope does not deploy, then what the scope's reads reported. */
export function pageNotices(capabilities: ResearchCapabilities | undefined, notices: ResearchNotice[] = []): PageNotice[] {
	return [...unavailableNotices(capabilities), ...notices].map((n) => ({ kind: n.kind, heading: noticeLabel(n), message: n.message }));
}

/** An owner's acknowledgment of a paragraph: it binds exactly the revisions the paragraph was shown at. */
export function acknowledgeRequest(item: ImpactItem, note?: string) {
	return { draftId: item.draftId, anchorId: item.anchorId, reviewed: item.current, ...(note ? { note } : {}) };
}

/** A scope's analysis summary as the page draws it: the same view a chat analysis_summary read gets. */
export function analysisPageView(scope: ResearchScopeRef, capabilities: ResearchCapabilities, summary: AnalysisSummary): ResearchView {
	const analysis = { runs: summary.runs, plans: summary.plans, inputs: summary.inputs, environments: summary.environments };
	return {
		section: "analysis_summary",
		operation: "analysis_summary",
		scopeRef: scope,
		snapshotAt: summary.snapshotAt,
		revision: summary.revision,
		records: [],
		notices: summary.notices,
		capabilities,
		targets: researchArtifactTargets(scope, [analysis]),
		analysis,
	};
}

/** The analysis row's counts. */
export function analysisCounts(summary: AnalysisSummary): string {
	const blocked = summary.runs.filter((r) => r.execution.status === "blocked").length;
	return [`${summary.runs.length} 次运行`, `${summary.plans.length} 个计划`, `${summary.inputs.length} 个输入`, blocked ? `${blocked} 次已阻止` : ""].filter(Boolean).join(" · ");
}
